import React, {useContext, useEffect, useRef, useState} from "react";
import styled from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faSearch} from "@fortawesome/free-solid-svg-icons";

// Contexts
import {AuthContext} from "../contexts/auth.js";

// Hooks
import useMountedState from "../hooks/useMountedState.js";

// Components
import CustomTables from "./general/CustomTables.js";
import GlobalTable from "./global-tables/GlobalTable.js";
import Room from "./general/Room.js";

// Styles
import {pad} from "../style/components/variables.js";
import {flex} from "../style/components/mixins.js";
import {Button, Page, Search, SearchIcon} from "../style/components/general.js";

const GlobalTables = () => {
  const isMounted = useMountedState();

  const {atLeast, isRole} = useContext(AuthContext);

  const [active, setActive] = useState(null);
  const [singleTableView, setSingleTableView] = useState(false);
  const [toggleAll, setToggleAll] = useState(false);
  const [allOpen, setAllOpen] = useState(false);
  const [query, setQuery] = useState(null);

  const [hasNoTables, setHasNoTables] = useState(undefined);

  const typing = useRef(false);

  const handleInputRateLimit = input => {
    if (typing.current) clearTimeout(typing.current);
    const timer = setTimeout(() => {
      setQuery(input);
      typing.current = null;
    }, 200);
    typing.current = timer;
  };

  // Initial Load - Handle View
  useEffect(() => {
    if (isMounted()) setSingleTableView(localStorage.getItem("globalTableView") === "single");
  }, [isMounted]);

  return (
    <Page>
      <Room name="global-tables" active={active} setActive={setActive} />

      <Menu>
        {atLeast("admin") && !isRole("Lab") && (
          <Button
            type="button"
            onClick={() => {
              localStorage.setItem("globalTableView", singleTableView ? "all" : "single");
              setSingleTableView(prev => !prev);
            }}>
            {singleTableView ? "View Facility Tables" : "View as Single Table"}
          </Button>
        )}
        {!singleTableView && (hasNoTables === false || !!query) && (
          <Option>
            <SearchIcon>
              <FontAwesomeIcon icon={faSearch} />
            </SearchIcon>
            <Search
              name="search"
              type="text"
              placeholder="Tables..."
              onChange={({target}) => handleInputRateLimit(target.value)}
            />
          </Option>
        )}
        {!singleTableView && hasNoTables === false && (
          <Button
            type="button"
            onClick={() => {
              setToggleAll(true);
              setAllOpen(prev => !prev);
            }}>
            {allOpen ? "Collapse" : "Expand"}&nbsp;All
          </Button>
        )}
      </Menu>

      {!singleTableView ? (
        <CustomTables
          inGlobalTables
          setHasNoTables={setHasNoTables}
          query={query}
          globalToggleAll={toggleAll}
          setGlobalToggleAll={setToggleAll}
          setGlobalAllOpen={setAllOpen}
        />
      ) : (
        <GlobalTable setHasNoTables={setHasNoTables} />
      )}
    </Page>
  );
};

// Style Overrides
const Menu = styled.div`
  ${flex("row", "wrap")};
  gap: ${pad}px;
  padding: ${pad}px 0;
  margin-bottom: ${pad / 2}px;
  width: 100%;
`;

const Option = styled.div`
  ${flex("row", "nowrap", "start", "center")};
  position: relative;
  height: 100%;
  margin-right: ${pad / 2}px;

  &:last-child {
    margin-right: 0;
  }
`;

export default GlobalTables;
